import { useContext, useEffect, useState } from "react";
import { FitToParentDiv } from "../ViewerUIElements/ResizeLayout/FitToParentDiv";
import { CoreViewer } from "./CoreViewer";
import { useViewerState } from "./useViewerState";
import { SessionContext } from "../common/contexts/SessionContext";
import { updateTypeInstance } from "./ViewerLayoutTypes";
import { useTranslatedFieldUpdate } from "./ViewerLayoutHooks";
import { Skeleton } from "../common/loaders/Skeleton/Skeleton";
import { ViewerLog } from "./ViewerLog";
import { ToolWindow } from "../ViewerUIElements/ToolWindow";
import { LucideBugPlay } from "lucide-react";
import { ParserDevelopmentTool } from "./ParserDevelopmentTool";
import { DatasetStructureViewer } from "../DatasetStructureViewer/DatasetStructureViewerNew";
import { ParameterComponent } from "../ParameterViewer/ParameterComponentNew";
import { TrackTypeTab } from "./TrackTypeTabNew";
import { HorizontalResizeDiv } from "../ViewerUIElements/ResizeLayout/HorizontalResizeDiv";
import { AutoResizeDiv, FittingDiv } from "../ViewerUIElements/ResizeLayout/AutoResizeDiv";
import styles from "./FullViewer.module.css";
import { VerticalResizeDiv } from "../ViewerUIElements/ResizeLayout/VerticalResizeDiv";
import { ParserDebugButton } from "./ParserDebugButton";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorFallback } from "../common/ErrorFallback";

export type FullViewerSettings = {
  showViewer: boolean;
  showParameter: boolean;
  showTrackList: boolean;
  showNavigation: boolean;
  interactiveMode: boolean;
};

export type FullViewerProps = {
  ids: number[];
  isViewerLoadingCallback?: (isLoading: boolean) => void;
  settings?: Partial<FullViewerSettings>;
};

const initFullViewerProps = (settings?: Partial<FullViewerSettings>): FullViewerSettings => {
  const result: FullViewerSettings = {
    showViewer: true,
    showParameter: true,
    showNavigation: true,
    showTrackList: true,
    interactiveMode: true,
  };
  if (settings) updateTypeInstance(result, settings);
  return result;
};

const NoViewerContent = () => (
  <Skeleton type="viewer" loading={false}>
    <div
      className="flex col-nowrap align-center justify-center"
      style={{ color: "var(--gray-500)", width: "100%", height: "100%", overflow: "hidden" }}
    >
      <span style={{ fontSize: "clamp(1rem, -1.5rem + 8vw, 3rem)" }}>No displayable data</span>
    </div>
  </Skeleton>
);

export const FullViewer = ({ ids, settings, isViewerLoadingCallback }: FullViewerProps) => {
  const viewerSettings = useTranslatedFieldUpdate(settings, initFullViewerProps, {});
  const [showParserToolWindow, setShowParserToolWindow] = useState<boolean>(true);

  var { session } = useContext(SessionContext);

  var viewerState = useViewerState(ids);

  const tracksAreLoading = useTranslatedFieldUpdate(
    viewerState.state.tracks,
    (tracks) =>
      viewerState.state.activeViewer !== undefined &&
      Object.values(tracks)
        .filter((t) => t.settings.active && (t.settings.visible ?? true))
        .some((t) => t.settings.state === "loading"),
    false,
    [viewerState.state.activeViewer]
  );

  useEffect(() => {
    if (isViewerLoadingCallback) isViewerLoadingCallback(tracksAreLoading);
  }, [tracksAreLoading, isViewerLoadingCallback]);

  useEffect(
    () =>
      viewerState.setViewerInteractionSettings({
        showNavigation: viewerSettings.showNavigation,
        interactiveMode: viewerSettings.interactiveMode,
      }),
    // log cleanup
    // TODO: check dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [viewerSettings.showNavigation, viewerSettings.interactiveMode]
  );

  const toolWindowSize = {
    width: 270,
    height: 80,
  };

  var tools =
    session?.features.parser_development_mode &&
    viewerState.state.viewPort.width > 0 &&
    viewerState.state.viewPort.height > 0 ? (
      viewerState.state.viewPort.width > toolWindowSize.width &&
      viewerState.state.viewPort.height > toolWindowSize.height ? (
        showParserToolWindow ? (
          <ToolWindow
            key={"debugTools"}
            defaultPosition={{
              x: viewerState.state.viewPort.width * 0.75,
              y: viewerState.state.viewPort.height * 0.05,
            }}
            width={toolWindowSize.width}
            height={toolWindowSize.height}
            parentWidth={viewerState.state.viewPort.width}
            parentHeight={viewerState.state.viewPort.height}
            top={viewerState.state.viewPort.top}
            left={viewerState.state.viewPort.left}
            id={"debugTools"}
            focused={true}
            onFocused={() => {}}
            onClosed={() => {
              setShowParserToolWindow(false);
            }}
            title={
              <div style={{ display: "flex", alignItems: "center" }}>
                <LucideBugPlay style={{ marginRight: 2, strokeWidth: undefined }} />
                <span style={{ marginLeft: "3px", marginBottom: 3 }}>Parser development Tools</span>
              </div>
            }
          >
            <ParserDevelopmentTool
              developmentMode={session?.features.development_mode ?? false}
              parserOutput={viewerState.parserOutput}
              fetchCurrentParserOutputs={viewerState.fetchCurrentParserOutputs}
              reparseCurrentDatasets={() => {
                viewerState.reparseCurrentDatasets(viewerState.forceReload);
              }}
            />
          </ToolWindow>
        ) : (
          <ParserDebugButton
            x={viewerState.state.viewPort.width * 0.95}
            y={viewerState.state.viewPort.height * 0.95}
            onClick={() => {
              setShowParserToolWindow(true);
            }}
          />
        )
      ) : null
    ) : null;

  if (viewerState.reparsing)
    return (
      <Skeleton type="viewer">
        <div className={styles.spinner}>
          <div>Reparsing...</div>
        </div>
      </Skeleton>
    );

  if (
    viewerState.state.viewerStatus.status !== "failed" &&
    viewerState.state.noTracks === undefined &&
    viewerState.state.noParameters === undefined
  )
    return (
      <>
        {viewerSettings.showParameter && !viewerSettings.showTrackList ? (
          <div className={styles.spinner}>
            <div>&nbsp;</div>
            <Skeleton type="rows" />
          </div>
        ) : (
          <Skeleton type="viewer">
            <div className={styles.spinner}>
              <div>&nbsp;</div>
            </div>
          </Skeleton>
        )}
      </>
    );

  if (viewerState.state.viewerStatus.status === "failed")
    return (
      <>
        {tools}
        <FitToParentDiv setViewPort={viewerState.setViewPort}>
          <Skeleton type="viewer" loading={false}>
            <ViewerLog
              status={viewerState.state.viewerStatus.status}
              logs={Object.values(viewerState.state.datasets)
                .map((d) => d.parserLogs)
                .flat()}
              datasetId={viewerState.ids?.[0] ?? undefined}
            />
          </Skeleton>
        </FitToParentDiv>
      </>
    );

  const viewer =
    viewerSettings.showViewer && Object.keys(viewerState.state.tracks).length > 0 ? (
      <CoreViewer {...viewerState} />
    ) : null;

  const parameter =
    viewerSettings.showParameter && viewerState.state.parameter.content.length > 0 ? (
      <ParameterComponent {...viewerState} />
    ) : null;

  const tracksTab =
    viewerSettings.showTrackList && Object.keys(viewerState.state.tracks).length > 0 ? (
      <TrackTypeTab {...viewerState} />
    ) : null;

  const tracks =
    viewerSettings.showTrackList && Object.keys(viewerState.state.tracks).length > 0 ? (
      <DatasetStructureViewer {...viewerState} />
    ) : null;

  const tracksAndTab = tracks ? (
    <>
      {tracksTab}
      {tracks}
    </>
  ) : null;

  let content = (
    <>
      {!viewerSettings.showParameter ? <NoViewerContent /> : <NoViewerContent />}
      {viewerState.state.parserLogs.length > 0 ? (
        <ViewerLog status="success" logs={viewerState.state.parserLogs} />
      ) : null}
    </>
  );

  if (viewer && tracks && parameter)
    content = (
      <>
        <FitToParentDiv setViewPort={viewerState.setViewPort}>
          <HorizontalResizeDiv rightWidth={300}>
            {viewer}
            <FittingDiv className={styles.autoResize}>
              {tracksTab}
              <div style={{ flexGrow: 1 }}>
                <AutoResizeDiv>
                  <VerticalResizeDiv>
                    {tracks}
                    {parameter}
                  </VerticalResizeDiv>
                </AutoResizeDiv>
              </div>
            </FittingDiv>
          </HorizontalResizeDiv>
        </FitToParentDiv>
      </>
    );
  else if (
    (viewer && !tracks && !parameter) ||
    (!viewer && tracks && !parameter) ||
    (!viewer && !tracks && parameter)
  ) {
    const show = viewer ?? tracksAndTab ?? parameter;
    return (
      <FitToParentDiv setViewPort={viewerState.setViewPort}>
        <FittingDiv>{show}</FittingDiv>
      </FitToParentDiv>
    );
  } else if (viewer && tracks && !parameter) {
    return (
      <>
        <FitToParentDiv setViewPort={viewerState.setViewPort}>
          <HorizontalResizeDiv rightWidth={300}>
            {viewer}
            <FittingDiv className={styles.autoResize}>
              {tracksTab}
              <div style={{ flexGrow: 1 }}>
                <AutoResizeDiv>{tracks}</AutoResizeDiv>
              </div>
            </FittingDiv>
          </HorizontalResizeDiv>
        </FitToParentDiv>
      </>
    );
  } else if (viewer && !tracks && parameter) {
    return (
      <>
        <FitToParentDiv setViewPort={viewerState.setViewPort}>
          <HorizontalResizeDiv rightWidth={300}>
            {viewer}
            <FittingDiv className={styles.autoResize}>
              <div style={{ flexGrow: 1 }}>
                <AutoResizeDiv>{parameter}</AutoResizeDiv>
              </div>
            </FittingDiv>
          </HorizontalResizeDiv>
        </FitToParentDiv>
      </>
    );
  }

  return (
    <>
      {tools}
      {content}
    </>
  );
};

export const Viewer = (props: FullViewerProps) => (
  <ErrorBoundary FallbackComponent={ErrorFallback}>
    <FullViewer {...props} />
  </ErrorBoundary>
);
